/**
 * Some browsers block local and session storage (private mode, embedded views): reading
 * `window.localStorage` or `window.sessionStorage` throws a SecurityError, or returns null. The app reads
 * storage in many places, so give the page an in-memory store instead. A
 * student can then sign in for the life of the page, and nothing throws.
 */
const createMemoryStorage = () => {
  const data = new Map();
  return {
    get length() {
      return data.size;
    },
    key: (index) => Array.from(data.keys())[index] ?? null,
    getItem: (key) => (data.has(String(key)) ? data.get(String(key)) : null),
    setItem: (key, value) => {
      data.set(String(key), String(value));
    },
    removeItem: (key) => {
      data.delete(String(key));
    },
    clear: () => data.clear(),
  };
};

const installFallback = (name) => {
  try {
    if (window[name]) {
      window[name].getItem("__storage_check__");
      return false;
    }
  } catch {
    /* blocked: use the fallback */
  }
  const memoryStorage = createMemoryStorage();
  try {
    Object.defineProperty(window, name, {
      configurable: true,
      value: memoryStorage,
    });
  } catch {
    return false;
  }
  return window[name] === memoryStorage;
};

export const installStorageFallback = () =>
  ["localStorage", "sessionStorage"].map(installFallback).some(Boolean);

installStorageFallback();
